import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Zap, Music, Download, Save } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr, runJob, fileUrl } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { useI18n } from "../lib/i18n";
import { money, moneySymbol, currencyFor } from "../lib/money";

const inp = "w-full rounded-lg border border-[#212230] bg-[#101118] px-3 py-2.5 text-[12.5px] outline-none focus:border-[#ff4b2b]/60";

export function StudioTemplates() {
  const { locale } = useI18n();
  const [d, setD] = useState({ templates: [], categories: [] });
  const [cat, setCat] = useState("");

  useEffect(() => {
    api.get(`/templates${cat ? `?category=${encodeURIComponent(cat)}` : ""}`)
      .then(({ data }) => setD(data)).catch(() => {});
  }, [cat]);

  return (
    <div className="p-6 lg:p-10" data-testid="studio-templates-page">
      <p className="overline">Templates · {d.total}</p>
      <h1 className="mt-3 font-display text-3xl font-semibold sm:text-4xl">
        {locale === "fr" ? "Presets de réalisateur" : "Director presets"}
      </h1>
      <div className="mt-7 flex flex-wrap gap-2">
        <button onClick={() => setCat("")} className={`rounded-full border px-4 py-2 text-[12px] font-semibold ${!cat ? "border-white bg-white text-black" : "border-[#2a2c3c] text-[#9194a6]"}`}>All</button>
        {d.categories.map((c) => (
          <button key={c.name} onClick={() => setCat(c.name)} data-testid={`studio-tpl-cat-${c.name.toLowerCase().replace(/\s/g, "-")}`} className={`rounded-full border px-4 py-2 text-[12px] font-semibold ${cat === c.name ? "border-white bg-white text-black" : "border-[#2a2c3c] text-[#9194a6] hover:text-white"}`}>
            {c.name} <span className="mono opacity-50">{c.count}</span>
          </button>
        ))}
      </div>
      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {d.templates.map((x) => (
          <div key={x.id} className="overflow-hidden rounded-xl border border-[#212230] bg-[#0b0b11]" data-testid={`studio-tpl-${x.slug}`}>
            <img src={x.thumbnail} alt={x.title} loading="lazy" className="h-32 w-full object-cover" />
            <div className="p-4">
              <p className="mono text-[10px] uppercase tracking-[0.14em] text-[#ff4b2b]">{x.category}</p>
              <p className="mt-1.5 text-[13px] font-medium">{x.title}</p>
              <Link to="/studio/create" className="btn-ghost mt-3 block rounded-full py-1.5 text-center text-[11px] font-semibold">
                {locale === "fr" ? "Utiliser" : "Use"}
              </Link>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AudioStudio() {
  const { locale } = useI18n();
  const [projects, setProjects] = useState([]);
  const [pid, setPid] = useState("");
  const [scene, setScene] = useState("");
  const [mood, setMood] = useState("cinematic");
  const [out, setOut] = useState(null);
  const [subs, setSubs] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => { api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {}); }, []);

  const design = async () => {
    setBusy("d");
    try {
      const { data } = await api.post("/ai/sound-design", {
        project_id: pid || null, scene_description: scene, mood, duration: 30,
      });
      setOut(data);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  const subtitles = async () => {
    if (!pid) return toast.error(locale === "fr" ? "Choisissez un projet" : "Pick a project");
    setBusy("s");
    try {
      const data = await runJob("/ai/subtitles", { project_id: pid, language: locale });
      setSubs(data);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  return (
    <div className="p-6 lg:p-10" data-testid="audio-page">
      <p className="overline">Audio studio</p>
      <h1 className="mt-3 font-display text-3xl font-semibold sm:text-4xl">
        {locale === "fr" ? "Musique, voix, SFX, sous-titres" : "Music, voice, SFX, subtitles"}
      </h1>

      <div className="mt-9 grid max-w-[1000px] gap-4 sm:grid-cols-2">
        <select className={inp} value={pid} onChange={(e) => setPid(e.target.value)} data-testid="audio-project">
          <option value="" children={locale === "fr" ? "Projet (optionnel)" : "Project (optional)"} />
          {projects.map((p) => <option key={p.id} value={p.id} children={p.title} />)}
        </select>
        <select className={inp} value={mood} onChange={(e) => setMood(e.target.value)}>
          {["cinematic", "suspense", "action", "romance", "horror", "epic", "ambient", "electronic"].map((m) => <option key={m} value={m} children={String(m)} />)}
        </select>
        <textarea rows={3} className={`${inp} sm:col-span-2`} value={scene} onChange={(e) => setScene(e.target.value)} data-testid="audio-scene" placeholder={locale === "fr" ? "Rue parisienne sous la pluie, pas qui s'approchent, tonnerre lointain…" : "Rainy Paris street, approaching footsteps, distant thunder…"} />
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        <button onClick={design} disabled={busy === "d" || !scene} data-testid="audio-generate" className="btn-amber inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[12.5px] font-semibold disabled:opacity-50">
          <Music className="h-4 w-4" /> {busy === "d" ? "…" : locale === "fr" ? "Générer le design sonore" : "Generate sound design"}
        </button>
        <button onClick={subtitles} disabled={busy === "s"} data-testid="subtitles-generate" className="btn-ghost rounded-full px-5 py-2.5 text-[12.5px] font-semibold disabled:opacity-50">
          {busy === "s" ? "…" : locale === "fr" ? "Générer les sous-titres" : "Generate subtitles"}
        </button>
      </div>

      {out && (
        <div className="mt-9 grid max-w-[1000px] gap-4 sm:grid-cols-2" data-testid="audio-result">
          <div className="rounded-xl border border-[#212230] bg-[#0b0b11] p-5">
            <p className="overline">Music</p>
            <p className="mt-2 text-[13px]">{out.music?.genre} · {out.music?.mood}</p>
            <p className="mt-2 text-[12.5px] text-[#9194a6]">{out.music?.description}</p>
            <div className="mt-4 flex h-16 items-end gap-1">
              {(out.music?.intensity_curve || []).map((v, i) => (
                <div key={i} className="flex-1 rounded-t bg-gradient-to-t from-[#ff4b2b] to-[#00d5ff]" style={{ height: `${Math.min(Math.max(v, 5), 100)}%` }} />
              ))}
            </div>
          </div>
          <div className="rounded-xl border border-[#212230] bg-[#0b0b11] p-5">
            <p className="overline">SFX & ambience</p>
            <ul className="mt-2 space-y-1.5">
              {(out.sfx || []).map((s, i) => (
                <li key={i} className="mono text-[11.5px] text-[#c0c3d0]">{String(s.at).padStart(4, "0")}s · {s.name}</li>
              ))}
              {(out.ambience || []).map((a, i) => <li key={`a${i}`} className="text-[12px] text-[#9194a6]">{a}</li>)}
            </ul>
            <p className="mt-4 text-[12px] text-[#9194a6]">{out.voice_direction}</p>
          </div>
        </div>
      )}

      {subs && (
        <div className="mt-8 max-w-[1000px]" data-testid="subtitles-result">
          <p className="overline">SRT</p>
          <pre className="mono mt-3 max-h-[320px] overflow-auto whitespace-pre-wrap rounded-xl border border-[#212230] bg-[#0b0b11] p-5 text-[11.5px] text-[#c0c3d0]">{subs.srt}</pre>
        </div>
      )}
    </div>
  );
}

export function ExportsPage() {
  const { locale } = useI18n();
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/exports").then(({ data }) => setList(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!list.some((e) => e.status === "rendering")) return;
    const t = setTimeout(load, 6000);
    return () => clearTimeout(t);
  }, [list]);

  const render = async (id) => {
    setBusy(id);
    try {
      await api.post(`/media/exports/${id}/render`);
      toast.success(locale === "fr" ? "Rendu lancé" : "Render started");
      load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(""); }
  };

  const TINT = { completed: "text-[#00ff9d]", ready: "text-[#ffc76b]", rendering: "text-[#00d5ff]" };

  return (
    <div className="p-6 lg:p-10" data-testid="exports-page">
      <p className="overline">Exports</p>
      <h1 className="mt-3 font-display text-3xl font-semibold sm:text-4xl">
        {locale === "fr" ? "Vos exports" : "Your exports"}
      </h1>
      <p className="mt-4 max-w-[640px] text-sm leading-relaxed text-[#9194a6]">
        {locale === "fr"
          ? "Le rendu assemble tous les plans terminés en un seul MP4 côté serveur, au format et à la résolution choisis, avec la musique et les sous-titres incrustés."
          : "Rendering muxes every completed shot into a single server-side MP4 at the chosen ratio and resolution, with music and burned-in subtitles."}
      </p>
      <div className="mt-9 space-y-2">
        {list.map((e) => (
          <div key={e.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#1a1b26] bg-[#0e0e15] p-4" data-testid={`export-item-${e.id}`}>
            <p className="mono text-[11px] uppercase text-[#9194a6]">
              <Download className="mr-2 inline h-3 w-3 text-[#ff4b2b]" />
              {e.format} · {e.resolution} · {e.aspect_ratio} · {e.shot_count} shots · {Math.round(e.duration)}s
            </p>
            <div className="flex items-center gap-3">
              <span className={`mono text-[10px] uppercase ${TINT[e.status] || "text-[#ff8a8a]"}`}>{e.status}</span>
              {e.status === "completed" && e.asset_id ? (
                <a href={fileUrl(e.asset_id)} download data-testid={`export-download-${e.id}`}
                  className="btn-amber inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-[11px] font-semibold">
                  <Download className="h-3 w-3" /> MP4
                </a>
              ) : (
                <button
                  disabled={busy === e.id || e.status === "rendering" || e.status === "empty"}
                  onClick={() => render(e.id)}
                  data-testid={`export-render-${e.id}`}
                  className="btn-ghost rounded-full px-4 py-1.5 text-[11px] font-semibold disabled:opacity-40">
                  {e.status === "rendering"
                    ? (locale === "fr" ? "Rendu…" : "Rendering…")
                    : (locale === "fr" ? "Rendre le film" : "Render film")}
                </button>
              )}
            </div>
            {e.error && <p className="w-full text-[11px] text-[#ff8a8a]">{e.error}</p>}
          </div>
        ))}
        {list.length === 0 && <p className="text-[13px] text-[#9194a6]">—</p>}
      </div>
    </div>
  );
}

function AutoRecharge() {
  const { locale } = useI18n();
  const fr = locale === "fr";
  const [ar, setAr] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/billing/auto-recharge").then(({ data }) => setAr(data)).catch(() => {});
  }, []);

  const save = async (patch) => {
    setBusy(true);
    try {
      const { data } = await api.put("/billing/auto-recharge", {
        enabled: ar.enabled, threshold: ar.threshold, pack_code: ar.pack_code,
        monthly_cap_eur: ar.monthly_cap_eur, ...patch,
      });
      setAr(data);
      toast.success(fr ? "Auto-recharge mise à jour" : "Auto-recharge updated");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  if (!ar) return null;
  const pack = ar.pack;

  return (
    <div className="mt-12 max-w-[760px] rounded-xl border border-[#212230] bg-[#0b0b11] p-6" data-testid="auto-recharge-panel">
      <div className="flex items-start justify-between gap-6">
        <div>
          <p className="font-display text-[15px] font-medium">
            {fr ? "Auto-recharge" : "Auto-recharge"}
          </p>
          <p className="mt-2 max-w-[460px] text-[12.5px] text-[#9194a6]">
            {fr
              ? `Quand votre solde passe sous le seuil, nous ajoutons automatiquement ${pack ? `${pack.credits.toLocaleString()} crédits pour ${money(pack.price, 'fr', 2)}` : "un pack"} sur la carte enregistrée. Un rendu ne s'arrête plus jamais en plein milieu.`
              : `When your balance drops below the threshold we add ${pack ? `${pack.credits.toLocaleString()} credits for ${money(pack.price, 'en', 2)}` : "a pack"} on your saved card. A render never stops mid-way again.`}
          </p>
        </div>
        <button
          onClick={() => save({ enabled: !ar.enabled })}
          disabled={busy}
          data-testid="auto-recharge-toggle"
          className={`relative h-7 w-12 shrink-0 rounded-full transition-colors duration-200 disabled:opacity-50 ${ar.enabled ? "bg-[#ff4b2b]" : "bg-[#2a2c3c]"}`}
        >
          <span className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-all duration-200 ${ar.enabled ? "left-6" : "left-1"}`} />
        </button>
      </div>

      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="mono text-[10px] uppercase tracking-[0.14em] text-[#8288a0]">
            {fr ? "Seuil de déclenchement" : "Trigger threshold"}
          </span>
          <input
            type="number" min={50} max={20000} className={`${inp} mt-2`}
            value={ar.threshold} data-testid="auto-recharge-threshold"
            onChange={(e) => setAr({ ...ar, threshold: Number(e.target.value) })}
            onBlur={() => save({ threshold: ar.threshold })}
          />
        </label>
        <label className="block">
          <span className="mono text-[10px] uppercase tracking-[0.14em] text-[#8288a0]">
            {fr ? "Plafond mensuel" : "Monthly cap"} ({moneySymbol(locale)})
          </span>
          <input
            type="number" min={0} max={10000} className={`${inp} mt-2`}
            value={ar.monthly_cap_eur} data-testid="auto-recharge-cap"
            onChange={(e) => setAr({ ...ar, monthly_cap_eur: Number(e.target.value) })}
            onBlur={() => save({ monthly_cap_eur: ar.monthly_cap_eur })}
          />
        </label>
      </div>

      <p className="mono mt-4 text-[10.5px] text-[#8288a0]" data-testid="auto-recharge-status">
        {fr ? "Dépensé ce mois" : "Spent this month"}: {money(ar.spent_this_month_eur, locale, 2)} ·{" "}
        {ar.card_on_file
          ? (fr ? "carte enregistrée" : "card on file")
          : (fr ? "aucune carte enregistrée — faites un achat une fois" : "no card on file — make one purchase first")}
      </p>
      {ar.last_error && (
        <p className="mt-2 text-[11px] text-[#ff8a8a]" data-testid="auto-recharge-error">{ar.last_error}</p>
      )}
    </div>
  );
}

export function CreditsPage() {
  const { locale } = useI18n();
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [cat, setCat] = useState({ packs: [] });
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/credits/summary").then(({ data }) => setS(data)).catch(() => {});
    api.get("/billing/catalog").then(({ data }) => setCat(data)).catch(() => {});
  }, []);

  const buy = async (lk) => {
    setBusy(lk);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: lk, origin_url: window.location.origin, currency: currencyFor(locale) });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiErr(e)); setBusy(""); }
  };

  return (
    <div className="p-6 lg:p-10" data-testid="credits-page">
      <p className="overline">Movies Credits</p>
      <h1 className="mt-3 flex items-center gap-3 font-display text-4xl font-semibold sm:text-5xl">
        <Zap className="h-8 w-8 text-[#ff4b2b]" />
        {Number(user.credits).toLocaleString()}
      </h1>
      <p className="mono mt-3 text-[11px] uppercase tracking-[0.16em] text-[#8288a0]">
        {locale === "fr" ? "Offre" : "Plan"} · {user.plan}
      </p>

      <div className="mt-10">
        <p className="overline">{locale === "fr" ? "Packs de crédits" : "Credit packs"}</p>
        <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {cat.packs.map((p) => (
            <button key={p.code} onClick={() => buy(p.lookup_key)} disabled={busy === p.lookup_key} data-testid={`credits-buy-${p.code}`} className="hot-border rounded-xl border border-[#212230] bg-[#0b0b11] p-5 text-left disabled:opacity-50">
              <p className="text-[13px] font-medium">{p.name}</p>
              <p className="mono mt-1.5 text-[11px] text-[#9194a6]">
                {money(p.price, locale, 2)}
              </p>
            </button>
          ))}
        </div>
      </div>

      <AutoRecharge />

      {s && (
        <div className="mt-12 max-w-[900px]">
          <p className="overline">{locale === "fr" ? "Journal des crédits" : "Credit ledger"}</p>
          <div className="mt-5 divide-y divide-[#16181f] rounded-xl border border-[#212230] bg-[#0b0b11]">
            {s.transactions.map((t) => (
              <div key={t.id} className="flex items-center justify-between p-4" data-testid={`ledger-${t.id}`}>
                <div>
                  <p className="text-[12.5px]">{t.reason}</p>
                  <p className="mono mt-0.5 text-[10px] uppercase text-[#8288a0]">{t.type} · {new Date(t.created_at).toLocaleString()}</p>
                </div>
                <p className={`mono text-[12.5px] ${t.amount >= 0 ? "text-[#00ff9d]" : "text-[#ff8a8a]"}`}>
                  {t.amount >= 0 ? "+" : ""}{t.amount}
                </p>
              </div>
            ))}
            {s.transactions.length === 0 && <p className="p-4 text-[13px] text-[#9194a6]">—</p>}
          </div>
        </div>
      )}
    </div>
  );
}

export function SettingsPage() {
  const { locale, setLocale } = useI18n();
  const { user, refresh } = useAuth();
  const [f, setF] = useState({ name: user.name, locale: user.locale, country: user.country || "" });
  const [history, setHistory] = useState([]);
  const [kits, setKits] = useState([]);
  const [kit, setKit] = useState({ name: "", colors: "", fonts: "", slogans: "", brand_style: "" });

  useEffect(() => {
    api.get("/payments/history").then(({ data }) => setHistory(data)).catch(() => {});
    api.get("/brand-kits").then(({ data }) => setKits(data)).catch(() => {});
  }, []);

  const save = async () => {
    try {
      await api.patch("/auth/me", f);
      if (f.locale) setLocale(f.locale);
      await refresh();
      toast.success("Saved");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const createKit = async () => {
    if (!kit.name) return;
    try {
      await api.post("/brand-kits", {
        name: kit.name,
        colors: kit.colors.split(",").map((s) => s.trim()).filter(Boolean),
        fonts: kit.fonts.split(",").map((s) => s.trim()).filter(Boolean),
        slogans: kit.slogans.split(",").map((s) => s.trim()).filter(Boolean),
        brand_style: kit.brand_style,
      });
      setKit({ name: "", colors: "", fonts: "", slogans: "", brand_style: "" });
      api.get("/brand-kits").then(({ data }) => setKits(data));
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="p-6 lg:p-10" data-testid="settings-page">
      <p className="overline">Settings</p>
      <h1 className="mt-3 font-display text-3xl font-semibold sm:text-4xl">
        {locale === "fr" ? "Compte & marque" : "Account & brand"}
      </h1>

      <div className="mt-9 grid max-w-[900px] gap-4 sm:grid-cols-2">
        <input className={inp} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="settings-name" />
        <input className={inp} value={user.email} disabled />
        <select className={inp} value={f.locale} onChange={(e) => setF({ ...f, locale: e.target.value })} data-testid="settings-locale">
          <option value="en">English</option>
          <option value="fr">Français</option>
        </select>
        <input className={inp} value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} placeholder="FR" data-testid="settings-country" />
        <button onClick={save} data-testid="settings-save" className="btn-primary inline-flex items-center justify-center gap-2 rounded-lg py-2.5 text-[13px] font-semibold">
          <Save className="h-4 w-4" /> {locale === "fr" ? "Enregistrer" : "Save"}
        </button>
      </div>

      <div className="mt-14 max-w-[900px]">
        <p className="overline">Brand Kit</p>
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          {[["name", "Name"], ["colors", "#111, #ff4b2b"], ["fonts", "Outfit, Manrope"], ["slogans", "Slogan 1, Slogan 2"], ["brand_style", "Brand style"]].map(([k, ph]) => (
            <input key={k} className={inp} placeholder={ph} value={kit[k]} onChange={(e) => setKit({ ...kit, [k]: e.target.value })} data-testid={`kit-${k}`} />
          ))}
          <button onClick={createKit} data-testid="kit-create" className="btn-ghost rounded-lg py-2.5 text-[13px] font-semibold">
            {locale === "fr" ? "Créer le Brand Kit" : "Create Brand Kit"}
          </button>
        </div>
        <div className="mt-5 space-y-2">
          {kits.map((k) => (
            <div key={k.id} className="flex items-center gap-4 rounded-lg border border-[#1a1b26] bg-[#0e0e15] p-4" data-testid={`kit-${k.id}`}>
              <p className="text-[13px] font-medium">{k.name}</p>
              <div className="flex gap-1.5">
                {(k.colors || []).map((c) => <span key={c} className="h-4 w-4 rounded-full border border-white/20" style={{ background: c }} />)}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-14 max-w-[900px]">
        <p className="overline">{locale === "fr" ? "Paiements" : "Billing history"}</p>
        <div className="mt-5 divide-y divide-[#16181f] rounded-xl border border-[#212230] bg-[#0b0b11]">
          {history.map((h) => (
            <div key={h.session_id} className="flex items-center justify-between p-4">
              <p className="mono text-[11.5px] text-[#9194a6]">{h.lookup_key} · {new Date(h.created_at).toLocaleDateString()}</p>
              <p className={`mono text-[11.5px] ${h.payment_status === "paid" ? "text-[#00ff9d]" : "text-[#9194a6]"}`}>
                {money(h.amount, locale, 2)} · {h.payment_status}
              </p>
            </div>
          ))}
          {history.length === 0 && <p className="p-4 text-[13px] text-[#9194a6]">—</p>}
        </div>
      </div>
    </div>
  );
}
